import { dateFormatter } from './dateFormatter';

export type FormatterFunction = (fullMatch: string, args: string[]) => string;
type KnownFormats = {
  [formatType: string]: FormatterFunction | undefined;
};

const FORMATTER_SYNTAX_REGEX = /{{(.*?)}}/g;

/**
 * The syntax for formatting is {{<format>|arg1|arg2|arg3|...|argN}}
 *
 * This formatter will look for the opening and closing double braces
 * and try to invoke any known formatter function that maps to <format>, returning
 * the original match if there is no known formatter function for that <format>.
 *
 * NOTE! There is no escaping! If you need to add a new formatter, consult your
 * use case with #creator-highlights first to see if special formatting is needed.
 */
export const KNOWN_FORMATTERS: KnownFormats = {
  date: dateFormatter,
};

export function formatString(rawInput: string): string {
  const match = rawInput.match(FORMATTER_SYNTAX_REGEX);

  if (!match) return rawInput;

  // match is now an array of parsed groups
  const formattedMatches = match.map(m => [m, m.replace(/[{}]+/g, '')]);

  return formattedMatches.reduce(
    (outString, [fullSyntaxMatch, innerSyntaxMatch]) => {
      const parts = innerSyntaxMatch.split('|');
      const [formatterType, ...args] = parts;
      const cleanedFormatterType = formatterType.trim();
      const formatter = KNOWN_FORMATTERS[cleanedFormatterType];

      if (!formatter) return outString;

      const cleanedArgs = args.map(a => a.trim());
      const formatted = formatter(fullSyntaxMatch, cleanedArgs);

      return outString.replace(fullSyntaxMatch, formatted);
    },
    rawInput,
  );
}
